import type { FormEvent, RefObject } from 'react'

import { PrivacyFineprint } from '@/components/client/shared/privacy-fineprint'
import { tw } from '@/lib/utils'

import type { SubscribeThemeTokens } from './theme'
import type { SubscribeFormContent } from './types'

export type SubscribeField = 'email' | 'name' | 'userType' | 'website'

const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden className={tw('h-4 w-4', className)}>
    <path d="M3 8h9M8.5 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const SpinnerIcon = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
)

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden className={tw('h-3.5 w-3.5', className)}>
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

interface FormCardProps {
  content: SubscribeFormContent
  tokens: SubscribeThemeTokens
  values: Record<SubscribeField, string>
  emailInvalid: boolean
  emailFocused: boolean
  emailInputRef: RefObject<HTMLInputElement | null>
  submitting: boolean
  errorMessage: string
  onChange: (field: SubscribeField, value: string) => void
  onEmailFocus: () => void
  onEmailBlur: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export const FormCard = ({
  content,
  tokens,
  values,
  emailInvalid,
  emailFocused,
  emailInputRef,
  submitting,
  errorMessage,
  onChange,
  onEmailFocus,
  onEmailBlur,
  onSubmit,
}: FormCardProps) => {
  const expanded = content.alwaysExpanded || emailFocused || values.email.length > 0
  const hasExtraFields = content.showNameField || content.showIdentityField
  const inputClass = tw(
    'w-full rounded-2xl border-0 px-4 py-3.5 text-[0.95rem] leading-[1.5] tracking-[-0.01em] outline-none transition-all',
    tokens.input
  )
  const labelClass = tw('mb-1.5 block text-[0.78rem] font-medium tracking-[-0.005em]', tokens.inputLabel)

  // Shared by both branches below — the fields render identically whether or
  // not they sit inside the collapsible track.
  const extraFields = (
    <>
      {content.showNameField && (
        <div>
          <label htmlFor="subscribe-name" className={labelClass}>
            {content.nameLabel}
          </label>
          <input
            id="subscribe-name"
            type="text"
            autoComplete="name"
            value={values.name}
            placeholder={content.namePlaceholder}
            onChange={(e) => onChange('name', e.target.value)}
            className={inputClass}
          />
        </div>
      )}
      {content.showIdentityField && content.identityOptions.length > 0 && (
        <div>
          <label htmlFor="subscribe-identity" className={labelClass}>
            {content.identityLabel}
          </label>
          <div className="relative">
            <select
              id="subscribe-identity"
              value={values.userType}
              onChange={(e) => onChange('userType', e.target.value)}
              className={tw(inputClass, 'cursor-pointer appearance-none pr-10', !values.userType && 'opacity-60')}
            >
              <option value="" disabled>
                {content.identityPlaceholder}
              </option>
              {content.identityOptions.map((option) => (
                <option key={option.value} value={option.value} className="text-navy">
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              className={tw('pointer-events-none absolute top-1/2 right-4 -translate-y-1/2', tokens.inputLabel)}
            />
          </div>
        </div>
      )}
    </>
  )

  return (
    <form onSubmit={onSubmit} className="flex flex-col" noValidate>
      {errorMessage && (
        <p className={tw('mb-3 rounded-xl px-4 py-3 text-[0.85rem] leading-[1.4]', tokens.errorBox)}>{errorMessage}</p>
      )}

      <div>
        <label htmlFor="subscribe-email" className={labelClass}>
          {content.emailLabel}
        </label>
        <input
          id="subscribe-email"
          ref={emailInputRef}
          type="email"
          autoComplete="email"
          inputMode="email"
          value={values.email}
          placeholder={content.emailPlaceholder}
          onChange={(e) => onChange('email', e.target.value)}
          onFocus={onEmailFocus}
          onBlur={onEmailBlur}
          className={tw(inputClass, emailInvalid && 'ring-error! focus:ring-error!')}
        />
        {emailInvalid && <span className="text-error mt-1.5 block text-[0.78rem]">{content.invalidEmailText}</span>}
      </div>

      {hasExtraFields &&
        // The reveal animates `grid-template-rows`, which needs an
        // `overflow-hidden` track — and that track CLIPS the inputs' 1px `ring`
        // (a box-shadow, painted outside the border box), so the left and right
        // edges of every field in here go missing. When the form never animates
        // there's no reason to pay that cost: render the fields directly.
        (content.alwaysExpanded ? (
          <div className="flex flex-col gap-3.5 pt-3.5">{extraFields}</div>
        ) : (
          <div
            className={tw(
              'grid transition-[grid-template-rows] duration-500 ease-out motion-reduce:transition-none',
              expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            <div className="overflow-hidden">
              <div className="flex flex-col gap-3.5 pt-3.5">{extraFields}</div>
            </div>
          </div>
        ))}

      {/* Honeypot — real users never fill this; the API treats a filled value as spam. */}
      <div className="pointer-events-none absolute left-[-9999px] opacity-0" aria-hidden="true">
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => onChange('website', e.target.value)}
        />
      </div>

      {/* Never rendered in a ghosted state — a disabled-looking button at rest
          read as "this form doesn't work" and produced zero submit attempts
          (528 viewers, 0 POSTs). Empty/invalid input is handled on submit. */}
      <button
        type="submit"
        disabled={submitting}
        data-tracking="ignore"
        className={tw(
          'mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl py-3.5 text-[0.92rem] font-semibold tracking-[-0.01em] transition-all duration-200',
          'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-wait disabled:opacity-80',
          tokens.button
        )}
      >
        {submitting ? (
          <>
            <SpinnerIcon />
            {content.submittingText}
          </>
        ) : (
          <>
            {content.buttonText}
            <ArrowRightIcon />
          </>
        )}
      </button>

      <PrivacyFineprint
        text={content.fineprint}
        className={tw('mt-3 text-[0.75rem] leading-[1.35] tracking-[-0.005em]', tokens.fineprint)}
      />
    </form>
  )
}
